<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    :title="$t('global.commission_note_create_title')"
    width="xl"
    hide-close-button
    tracking-name="CommissionNoteCreateSlideOver"
    @close="close"
  >
    <template #body>
      <div class="flex min-h-0 w-full flex-col gap-4">
        <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          <section
            class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
          >
            <h4
              class="text-xs font-semibold tracking-wide text-gray-500 uppercase"
            >
              {{ $t('global.informations') }}
            </h4>
            <SelectComponent
              v-model="userId"
              :values="advisors"
              display-key="full_name"
              :label="$t('attributes.advisor')"
              name="user_id"
              :errors="errors.user_id"
              edition-mode
              searchable
              required
            />
            <SelectFuneralHome
              v-model="funeralHomeId"
              :label="$t('attributes.funeral_home')"
              name="funeral_home_id"
              :errors="errors.funeral_home_id"
              edition-mode
              required
            />
          </section>
          <section
            class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
          >
            <h4
              class="text-xs font-semibold tracking-wide text-gray-500 uppercase"
            >
              {{ $t('global.options') }}
            </h4>
            <BaseShowEditInput
              v-model="day"
              type="date"
              :label="$t('attributes.date')"
              name="day"
              :errors="errors.day"
              edition-mode
            />
            <SelectLanguages
              v-model="language"
              :label="$t('attributes.language')"
              name="language"
              edition-mode
              customer-language-only
            />
          </section>
        </div>
        <div class="flex shrink-0 justify-end">
          <BaseButton
            :disabled="saving || !userId || !funeralHomeId"
            :display-loader="saving"
            :title="$t('button.create')"
            @click="create"
          />
        </div>
      </div>
    </template>
    <template #cta><span /></template>
  </BaseSlideOverModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import SelectLanguages from '@c/InitialStateFormComponent/Enums/SelectLanguages.vue'
import SelectFuneralHome from '@c/InitialStateFormComponent/SelectFuneralHome.vue'
import { defaultPeriod } from '@/views/invoicing/show/commissionNote/components/commissionNoteCandidates'

/**
 * Creates the open note of an advisor for an agency and a day, or opens the
 * one that already exists for that period (the API answers `created`).
 */
export default {
  name: 'CommissionNoteCreateSlideOver',
  components: {
    BaseButton,
    BaseSlideOverModal,
    BaseShowEditInput,
    SelectComponent,
    SelectFuneralHome,
    SelectLanguages
  },
  props: {
    open: { type: Boolean, required: true }
  },
  emits: ['update:open', 'close', 'created'],
  data() {
    return {
      userId: null,
      funeralHomeId: null,
      language: null,
      day: defaultPeriod().to,
      saving: false
    }
  },
  computed: {
    ...mapGetters({
      users: 'metadata/getUsersMetadata',
      errors: 'auth/getErrors'
    }),
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    // Only an advisor who earns commissions can have a note (the API refuses the others)
    advisors() {
      return (this.users ?? []).filter(
        (user) => user.receives_commission !== false
      )
    }
  },
  methods: {
    ...mapActions({
      processStoreAction: 'commissionNote/storeCommissionNote'
    }),
    close() {
      this.$emit('close')
    },
    create() {
      this.saving = true
      this.processStoreAction({
        user_id: this.userId,
        funeral_home_id: this.funeralHomeId,
        day: this.day || undefined,
        language: this.language || undefined
      })
        .then((response) => {
          this.$emit('created', response)
          this.close()
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>
